import { Form, Formik } from 'formik'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { resetPassword } from '../../utils/api'
import { useAppHistory } from '../../utils/useAppHistory'
import { PasswordInput } from '../form/FormikWrappers'
import Button from '../ui/Button'
import FormButtons from '../ui/FormButtons'
import { AuthPage } from '../ui/PageTemplate'
import AuthLinks from './AuthLinks'
import { ErrorMessage } from './AuthWrappers'
import { withAuthRedirect } from './withAuthRedirect'

const getResetToken = () =>
  new URLSearchParams(window.location.search).get('token')

const PasswordSetPage = () => {
  const history = useAppHistory()
  const { t } = useTranslation()

  useEffect(() => {
    if (!getResetToken()) {
      toast.error(t('devise.passwords.no_token'), { autoClose: 5000 })
      history.push('/auth/sign_in')
    }
  }, [history, t])

  const handleSubmit = async ({ password }) => {
    try {
      const { email } = await resetPassword({
        password: password,
        token: getResetToken(),
      })
      toast.success(t('devise.passwords.updated_not_active'), {
        autoClose: 5000,
      })
      history.push({ pathname: '/auth/sign_in', state: { email } })
    } catch (error) {
      toast.error(
        t('error_message.auth.password_set_failed', {
          message: error.message || t('error_message.unknown_error'),
        }),
      )
      history.push('/auth/sign_in')
    }
  }

  return (
    <AuthPage>
      <h1>{t('users.change_password')}</h1>
      <Formik
        initialValues={{
          password: '',
          password_confirm: '',
        }}
        validationSchema={Yup.object({
          password: Yup.string().min(6).required(),
          password_confirm: Yup.string()
            .oneOf([Yup.ref('password')])
            .required(),
        })}
        onSubmit={handleSubmit}
      >
        {({ errors, dirty, isValid, isSubmitting, values }) => (
          <Form>
            <div>
              <PasswordInput name="password" label={t('users.new_password')} />
              {errors.password && (
                <ErrorMessage>
                  {t(errors.password.key, errors.password.options)}
                </ErrorMessage>
              )}

              <PasswordInput
                name="password_confirm"
                label={t('users.new_password_confirmation')}
                invalidWhenUntouched={values.password !== ''}
              />
              {errors.password_confirm && (
                <ErrorMessage>
                  {t(
                    errors.password_confirm.key,
                    errors.password_confirm.options,
                  )}
                </ErrorMessage>
              )}
            </div>

            <FormButtons>
              <Button
                disabled={!dirty || !isValid || isSubmitting}
                type="submit"
              >
                {t('users.change_password')}
              </Button>
            </FormButtons>
          </Form>
        )}
      </Formik>
      <AuthLinks include={['signIn']} />
    </AuthPage>
  )
}

export default withAuthRedirect(PasswordSetPage)
